/**
 * Session Guard — global fetch-interceptor som fanger session-timeout
 *
 * Monkey-patcher window.fetch slik at ALLE 401-responser fra `/api/*`
 * trigger en tydelig modal + redirect til hovedside med påloggingsmelding.
 *
 * Uten dette får brukeren "tomme skall og feil innhold" når session går ut
 * mens de er på en innlogget side.
 */

let modalShown = false;
let redirecting = false;

/**
 * Vis en tydelig modal + redirect etter kort delay.
 * Idempotent: trigges kun én gang per session-utløp.
 */
function triggerSessionExpired(reason: 'timeout' | 'invalid' = 'timeout') {
  if (modalShown || redirecting) return;
  modalShown = true;

  // Bygg modalen direkte i DOM — ingen React-dependency (vi vil vise selv om tree er i feil state)
  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'alertdialog');
  overlay.setAttribute('aria-labelledby', 'session-expired-title');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'background:rgba(15,23,42,0.72)',
    'z-index:999999',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:16px',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    'backdrop-filter:blur(4px)'
  ].join(';');

  const panel = document.createElement('div');
  panel.style.cssText = [
    'background:white',
    'border-radius:12px',
    'box-shadow:0 20px 50px rgba(0,0,0,0.3)',
    'max-width:420px',
    'width:100%',
    'padding:32px 28px',
    'text-align:center'
  ].join(';');

  const icon = document.createElement('div');
  icon.innerHTML = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
  icon.style.cssText = 'margin:0 auto 16px;display:flex;justify-content:center';

  const title = document.createElement('h2');
  title.id = 'session-expired-title';
  title.textContent = reason === 'invalid' ? 'Innlogging kreves' : 'Økten har utløpt';
  title.style.cssText = 'margin:0 0 12px;font-size:1.4rem;color:#033459;font-weight:600';

  const message = document.createElement('p');
  message.textContent = reason === 'invalid'
    ? 'Du må være innlogget for å se dette innholdet. Trykk på knappen under for å logge inn med Vipps.'
    : 'Av sikkerhetsgrunner er du blitt logget ut. Logg inn på nytt med Vipps for å fortsette.';
  message.style.cssText = 'margin:0 0 24px;color:#475569;line-height:1.5;font-size:1rem';

  const btn = document.createElement('button');
  btn.textContent = 'Logg inn på nytt';
  btn.style.cssText = [
    'background:#f97316',
    'color:white',
    'border:none',
    'border-radius:8px',
    'padding:12px 24px',
    'font-size:1rem',
    'font-weight:600',
    'cursor:pointer',
    'width:100%',
    'transition:background 0.15s'
  ].join(';');
  btn.onmouseover = () => (btn.style.background = '#ea580c');
  btn.onmouseout = () => (btn.style.background = '#f97316');
  btn.onclick = () => {
    redirecting = true;
    window.location.href = '/?error=session_expired&message=' +
      encodeURIComponent('Økten din har utløpt. Vennligst logg inn på nytt.');
  };

  panel.appendChild(icon);
  panel.appendChild(title);
  panel.appendChild(message);
  panel.appendChild(btn);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  // Auto-redirect etter 20 sek dersom brukeren ikke klikker
  setTimeout(() => {
    if (!redirecting) {
      redirecting = true;
      window.location.href = '/?error=session_expired&message=' +
        encodeURIComponent('Økten din har utløpt. Vennligst logg inn på nytt.');
    }
  }, 20000);
}

/**
 * Installer global fetch-interceptor.
 * Alle 401-responser fra /api/candidate/* eller /api/certificates/{id}/*
 * (dvs. autentiserte endepunkter) trigger session-expired-modalen.
 *
 * Public endepunkter som /api/certificates/verify/* ekskluderes.
 */
// Lagret referanse til original fetch — brukes av verifyAndTriggerIfExpired for å
// unngå rekursjon (session-check må ikke selv trigge session-guard).
let savedOriginalFetch: typeof window.fetch | null = null;

export function installSessionGuard() {
  const originalFetch = window.fetch.bind(window);
  savedOriginalFetch = originalFetch;

  const PROTECTED_PATHS = [
    '/api/candidate/',
    '/api/europass-edci/',
    '/api/cv/',
    '/api/certificates/'
  ];

  // Public endepunkter som returnerer 401 lovlig uten å bety session-timeout.
  // Hele /auth/-undertreet er auth-flyten selv (login, password, remember-token osv.),
  // inkludert /auth/session som er en password-sesjonssjekk som returnerer 401 for
  // Vipps-innloggede uten passord-step. Den endepointen må ikke trigge modal.
  const PUBLIC_PATHS = [
    '/api/certificates/verify/',
    '/api/certificates/share/',
    '/api/candidate/auth/'
  ];

  // Sider der vi ALDRI viser session-modal (ellers går vi i loop når redirect-destinasjonen
  // selv prøver å hente data og får 401).
  // Fix 2026-10-08 (Kent SEB-test incident): /seb-test, /register, /min-pamelding
  // og /venteliste er public ruter som ikke krever Vipps-session fra start —
  // Layout.tsx's bakgrunns-session-check må ikke trigge "økten har utløpt"-modal
  // på disse sidene.
  const SKIP_ON_PATHS = [
    '/',
    '/login',
    '/claim',
    '/verify',
    '/share',
    '/seb-test',
    '/register',
    '/min-pamelding',
    '/venteliste',
    '/welcome',
    '/om-kvalitetssikring',
    '/404'
  ];

  window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const response = await originalFetch(input, init);

    // Finn URL-sti for sjekk mot allowlist/denylist
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.pathname
        : input.url;

    let pathname: string;
    try {
      pathname = new URL(url, window.location.origin).pathname;
    } catch {
      pathname = url;
    }

    const isProtected = PROTECTED_PATHS.some(p => pathname.startsWith(p));
    const isPublic = PUBLIC_PATHS.some(p => pathname.startsWith(p));

    // Behandle kun potensielle session-timeouts
    if (response.status !== 401) return response;

    // Hopp over hvis bruker allerede er på login-/public-sider
    const currentPath = window.location.pathname;
    if (SKIP_ON_PATHS.some(p => currentPath === p || currentPath.startsWith(p + '/'))) {
      return response;
    }

    // Hopp over hvis dette er et public endepunkt
    if (isPublic) return response;

    // Modal-trigging:
    //  - Hvis vi har bevis på at bruker VAR autentisert (hasBeenAuthenticated), er 401
    //    nesten alltid "session utløpt" → verifiser og trigg.
    //  - Hvis vi IKKE har sett autentisering ennå, men bruker er på en beskyttet side
    //    (ikke i SKIP_ON_PATHS), er dette typisk tilfellet der mobilen suspenderte fanen
    //    i >30 min og JS-staten er fersk ved retur. Vi vil fortsatt trigge modal her —
    //    men først VERIFISERE via /session for å unngå falsk alarm på f.eks. en direkte-
    //    linket beskyttet side der bruker aldri var logget inn.
    //
    // SKIP_ON_PATHS-sjekken ovenfor har allerede filtrert bort landings-/login-sider,
    // så vi kan trygt verifisere mot /session her.
    if (isProtected) {
      verifyAndTriggerIfExpired();
    }

    return response;
  };
}

/**
 * Bekrefter session-timeout via eksplisitt session-check før modal vises.
 * Idempotent: andre samtidige kall hopper over hvis en verifisering er under gang.
 */
let verifying = false;
async function verifyAndTriggerIfExpired() {
  if (verifying || modalShown || redirecting) return;
  if (!savedOriginalFetch) return; // guard ikke installert
  verifying = true;
  try {
    // Bruk /api/candidate/session (ikke /auth/session — den er en annen flyt som
    // returnerer 401 lovlig for Vipps-innloggede sessions uten password/auth-step).
    const res = await savedOriginalFetch('/api/candidate/session', {
      credentials: 'include'
    });
    // 401 på session-check = session er virkelig borte
    // 200 = session fortsatt gyldig (det var en falsk 401 på et annet endepunkt)
    if (res.status === 401) {
      triggerSessionExpired('timeout');
    }
  } catch (err) {
    // Nettverksfeil — ikke trigg modal (bruker kan jo bare være offline)
    console.warn('[SessionGuard] session-check failed, not triggering modal:', err);
  } finally {
    verifying = false;
  }
}
